import { useState, useEffect } from "react";
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  where,
  getDocs
} from "firebase/firestore";
import { json2csv } from "json-2-csv";

import {
  Box,
  Grid,
  Typography,
  TableRow,
  TableCell,
  Tooltip,
  Button,
  CircularProgress,
  useTheme
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import AdminEventUploadForm from "./AdminEventUploadForm";
import SortableTable, {
  makeColumnObj
} from "../../modules/tables/SortableTable";

import {
  getTaskStatusDetails,
  formatTimestamp,
  getFileNameFromPath
} from "../../../helpers/components/tasks";
import { db, useAuth } from "../../../helpers/firebase";

const useAdminEventReports = (userEmail) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userEmail) return;

    const reportsRef = collection(db, "admin-event-reports");
    const q = query(
      reportsRef,
      where("initiatedByEmail", "==", userEmail),
      orderBy("startTime", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id
        }));
        setReports(data);
        setLoading(false);
      },
      (err) => {
        console.error("Failed to fetch admin event reports:", err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userEmail]);

  return [reports, loading];
};

const EventTaskDocRow = ({ taskDoc }) => {
  const { startTime, type, filePath, status, companyName, companyId, taskId } =
    taskDoc;
  const { palette } = useTheme();
  const [downloading, setDownloading] = useState(false);

  const { icon, color, summary, spin } = getTaskStatusDetails(
    taskDoc,
    0,
    palette
  );
  const fileName = getFileNameFromPath(filePath, type);

  // TODO (backend): Column order is scrambled -- Firestore docs have no field
  // order guarantee, so original CSV columns get mixed with calculated fields.
  // Fix if needed: store the original header order on the task doc and use it
  // to sort columns on download.
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const dataRef = collection(
        db,
        `v3-companies/${companyId}/tasks/${taskId}/taskData`
      );
      const snapshot = await getDocs(query(dataRef));
      const rows = snapshot.docs.map((doc) => {
        const data = doc.data();
        return Object.fromEntries(
          Object.entries(data).map(([key, value]) => [
            key,
            value === undefined || value === null ? "" : value
          ])
        );
      });

      if (rows.length === 0) {
        console.warn("No data rows found for this task.");
        setDownloading(false);
        return;
      }

      const allKeys = [...new Set(rows.flatMap(Object.keys))];
      const normalizedRows = rows.map((row) =>
        Object.fromEntries(
          allKeys.map((key) => [key, key in row ? row[key] : ""])
        )
      );

      const csv = json2csv(normalizedRows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const baseName = (fileName || companyName || "event").replace(
        /\.csv$/i,
        ""
      );
      link.download = `${baseName}-results.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download:", err);
    }
    setDownloading(false);
  };

  return (
    <TableRow>
      <TableCell>
        <Tooltip title={summary}>
          <span>
            <FontAwesomeIcon
              icon={icon}
              color={color}
              style={{ width: "20px", height: "20px" }}
              spin={spin}
            />
          </span>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{companyName || "\u2014"}</Typography>
      </TableCell>
      <TableCell>{formatTimestamp(startTime)}</TableCell>
      <TableCell>
        <Typography variant="body2">{fileName}</Typography>
      </TableCell>
      <TableCell>
        {status === "finished" && companyId && taskId && (
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={handleDownload}
            disabled={downloading}
          >
            {downloading ? "Downloading..." : "Download CSV"}
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
};

const processTableColumns = [
  makeColumnObj("STATUS", "status", true),
  makeColumnObj("COMPANY", "companyName", true),
  makeColumnObj("START TIME", "startTime", true),
  makeColumnObj("FILE NAME", "fileName", true),
  makeColumnObj("ACTION", "action", false)
];

const EventReportingPage = () => {
  const [user] = useAuth();
  const [reports, reportsLoading] = useAdminEventReports(user?.email);
  const [formOpen, setFormOpen] = useState(false);
  const [pendingReport, setPendingReport] = useState(null);

  const handleFormClose = (uploadedReport) => {
    if (uploadedReport?.companyId) setPendingReport(uploadedReport);
    setFormOpen(false);
  };

  useEffect(() => {
    if (
      pendingReport &&
      reports.some((r) => r.filePath === pendingReport.filePath)
    ) {
      setPendingReport(null);
    }
  }, [reports, pendingReport]);

  const displayReports = pendingReport
    ? [{ ...pendingReport, id: "pending" }, ...reports]
    : reports;

  return (
    <Box>
      {formOpen && (
        <AdminEventUploadForm open={formOpen} onClose={handleFormClose} />
      )}
      <Grid container spacing={3} direction="column">
        <Grid item>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h4">Event Reporting</Typography>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={() => setFormOpen(true)}
              >
                Add New
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          {reportsLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : displayReports.length > 0 ? (
            <SortableTable
              rows={displayReports}
              rowComponent={(taskDoc) => (
                <EventTaskDocRow key={taskDoc.id} taskDoc={taskDoc} />
              )}
              columns={processTableColumns}
            />
          ) : (
            <Typography variant="body1" color="textSecondary">
              No event reports yet. Click &quot;Add New&quot; to upload an event CSV.
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default EventReportingPage;
